import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Stack,
  Divider,
  Switch,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InvestorConnectWalletDialog, {
  useDisconnectLinkedWallet,
} from "./InvestorConnectWalletDialog";
import { useAppSelector } from "../../redux/hook";
import {
  formatRoleLabel,
  getUserAvatarInitials,
  getUserDisplayName,
} from "../../utils/userDisplay";
import { authStorageGet } from "../../utils/authStorage";
import {
  useGetInvestorProfileQuery,
  useNafathDocumentsMutation,
} from "../../redux/api/authApi";

const WALLET_CHAIN_ID = Number(process.env.REACT_APP_WALLET_CHAIN_ID) || 1;

function blockExplorerAddressUrl(address) {
  const a = String(address || "");
  if (!a) return "#";
  if (WALLET_CHAIN_ID === 137) return `https://polygonscan.com/address/${a}`;
  if (WALLET_CHAIN_ID === 56) return `https://bscscan.com/address/${a}`;
  return `https://etherscan.io/address/${a}`;
}

function networkLabel(chainId) {
  if (chainId === 137) return "Polygon Network";
  if (chainId === 56) return "BNB Chain";
  if (chainId === 1) return "Ethereum Mainnet";
  return `Chain ID: ${chainId}`;
}

function formatProfileDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function formatIdTypeLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatNationalityLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function normalizeNafathFileKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function nafathFileLabel(value) {
  const key = normalizeNafathFileKey(value);
  if (key === "idproofdocument" || key === "idproof") return "ID Proof Document";
  if (key === "addressproofdocument" || key === "addressproof")
    return "Address Proof Document";
  return String(value || "Document");
}

function InvestorSettings() {
  const user = useAppSelector((state) => state.auth?.user);
  const {
    data: profileRes,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErr,
    refetch: refetchProfile,
  } = useGetInvestorProfileQuery(undefined, {
    skip: String(user?.role ?? "").toLowerCase() !== "investor",
  });
  const [uploadNafathDocuments, { isLoading: isUploadingNafathDocuments }] =
    useNafathDocumentsMutation();

  const profile = useMemo(() => {
    const root = profileRes;
    if (!root || typeof root !== "object") return null;
    return root.data ?? null;
  }, [profileRes]);

  const mergedUser = useMemo(() => {
    if (!profile) return user;
    return {
      ...user,
      firstName: profile.firstName ?? user?.firstName,
      lastName: profile.lastName ?? user?.lastName,
      email: profile.email ?? user?.email,
    };
  }, [user, profile]);

  const displayName = getUserDisplayName(mergedUser);
  const roleLabel = formatRoleLabel(profile?.role ?? user?.role);
  const initials = getUserAvatarInitials(mergedUser);

  // simple local state for switches (hydrated from GET profile)
  const [twoFA, setTwoFA] = useState(false);
  const [biometric, setBiometric] = useState(false);
  const [portfolioVisible, setPortfolioVisible] = useState(false);
  const [activityTracking, setActivityTracking] = useState(false);
  const [thirdPartySharing, setThirdPartySharing] = useState(false);
  const [autoReinvest, setAutoReinvest] = useState(false);
  const [newPropertyAlerts, setNewPropertyAlerts] = useState(false);
  const [emailChannel, setEmailChannel] = useState(true);
  const [smsChannel, setSmsChannel] = useState(false);
  const [pushChannel, setPushChannel] = useState(false);
  const [payoutUpdates, setPayoutUpdates] = useState(true);
  const [tokenIssuance, setTokenIssuance] = useState(true);
  const [complianceAlerts, setComplianceAlerts] = useState(true);
  const [propertyUpdates, setPropertyUpdates] = useState(false);
  const [marketingPromos, setMarketingPromos] = useState(false);

  useEffect(() => {
    if (!profile || typeof profile !== "object") return;
    setTwoFA(!!profile.twoFactorAuthentication);
    setBiometric(!!profile.biometricAuthentication);
    setPortfolioVisible(!!profile.portfolioVisibility);
    setActivityTracking(!!profile.activeTracking);
    setThirdPartySharing(!!profile.thirdPartyDataSharing);
    setAutoReinvest(!!profile.autoReinvestDividends);
    setNewPropertyAlerts(!!profile.newPropertyAlerts);
    setEmailChannel(profile.emailNotifications !== false);
    setSmsChannel(!!profile.smsNotifications);
    setPushChannel(!!profile.pushNotifications);
    setPayoutUpdates(profile.payoutUpdates !== false);
    setTokenIssuance(profile.tokenIssuance !== false);
    setComplianceAlerts(profile.complianceAlerts !== false);
    setPropertyUpdates(!!profile.propertyUpdates);
    setMarketingPromos(!!profile.marketingPromotions);
  }, [profile]);

  const [walletAddress, setWalletAddress] = useState(
    () => authStorageGet("investor_wallet_address") || ""
  );
  const [walletProvider, setWalletProvider] = useState(
    () => authStorageGet("investor_wallet_provider") || ""
  );
  const [connectOpen, setConnectOpen] = useState(false);
  const [isEditingNafathDocs, setIsEditingNafathDocs] = useState(false);
  const [idProofFile, setIdProofFile] = useState(null);
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [nafathDocErrors, setNafathDocErrors] = useState({});
  const idProofInputRef = useRef(null);
  const addressProofInputRef = useRef(null);
  const disconnectLinked = useDisconnectLinkedWallet();

  // Seed wallet address from API profile if not already set via localStorage
  useEffect(() => {
    if (profile?.walletAddress && !authStorageGet("investor_wallet_address")) {
      setWalletAddress(profile.walletAddress);
    }
  }, [profile?.walletAddress]);

  const handleWalletLinked = useCallback(({ address, provider }) => {
    setWalletAddress(address || "");
    setWalletProvider(provider || "");
  }, []);

  const handleDisconnectWallet = useCallback(async () => {
    await disconnectLinked(walletAddress);
    setWalletAddress("");
    setWalletProvider("");
  }, [walletAddress, disconnectLinked]);

  const apiWalletVerified = !!profile?.walletVerified;
  const apiWalletVerifiedAt = profile?.walletVerifiedAt ?? null;
  const nafathStatusText = profile?.nafathStatus || "N/A";
  const nafathRejectionReason = String(profile?.nafathRejectionReason || "").trim();
  const nafathFilesNeedToBeUpdatedRaw = Array.isArray(profile?.nafathFilesNeedToBeUpdated)
    ? profile.nafathFilesNeedToBeUpdated
    : [];
  const normalizedNafathFilesToUpdate = nafathFilesNeedToBeUpdatedRaw.map(
    normalizeNafathFileKey
  );
  const shouldUpdateIdProof = normalizedNafathFilesToUpdate.some(
    (key) => key === "idproofdocument" || key === "idproof"
  );
  const shouldUpdateAddressProof = normalizedNafathFilesToUpdate.some(
    (key) => key === "addressproofdocument" || key === "addressproof"
  );
  const hasNafathDocUpdateRequest = shouldUpdateIdProof || shouldUpdateAddressProof;
  const showNafathRejectionDetails =
    Boolean(nafathRejectionReason) || normalizedNafathFilesToUpdate.length > 0;

  useEffect(() => {
    if (!hasNafathDocUpdateRequest) {
      setIsEditingNafathDocs(false);
      setIdProofFile(null);
      setAddressProofFile(null);
      setNafathDocErrors({});
    }
  }, [hasNafathDocUpdateRequest]);

  const onNafathFilePicked = (event, fileType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setNafathDocErrors((prev) => ({
        ...prev,
        [fileType]: "File size must not exceed 10 MB.",
      }));
      event.target.value = "";
      return;
    }
    if (fileType === "idProofDocument") {
      setIdProofFile(file);
    } else if (fileType === "addressProofDocument") {
      setAddressProofFile(file);
    }
    setNafathDocErrors((prev) => ({ ...prev, [fileType]: "", submit: "" }));
  };

  const handleSubmitNafathDocuments = async () => {
    const newErrors = {};
    if (shouldUpdateIdProof && !idProofFile) {
      newErrors.idProofDocument = "Please upload ID proof document.";
    }
    if (shouldUpdateAddressProof && !addressProofFile) {
      newErrors.addressProofDocument = "Please upload address proof document.";
    }
    if (
      !shouldUpdateIdProof &&
      !shouldUpdateAddressProof &&
      !idProofFile &&
      !addressProofFile
    ) {
      newErrors.submit = "No document requested for update by admin.";
    }

    setNafathDocErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();
    if (idProofFile) formData.append("idProofDocument", idProofFile);
    if (addressProofFile) formData.append("addressProofDocument", addressProofFile);

    try {
      await uploadNafathDocuments(formData).unwrap();
      await refetchProfile();
      setIsEditingNafathDocs(false);
      setIdProofFile(null);
      setAddressProofFile(null);
      setNafathDocErrors({});
    } catch (error) {
      setNafathDocErrors((prev) => ({
        ...prev,
        submit:
          error?.data?.message ||
          error?.data?.error ||
          error?.error ||
          "Could not upload Nafath documents.",
      }));
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {profileLoading && !profile ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : null}
        {profileError ? (
          <Alert severity="error" onClose={() => refetchProfile()} sx={{ mb: 2 }}>
            {profileErr?.data?.message || profileErr?.error || "Could not load profile."}
          </Alert>
        ) : null}

        {/* Back link */}
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "text.primary",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        {/* Page title */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: 1,
              bgcolor: "background.headerIcon",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SettingsOutlinedIcon sx={{ color: "text.slate" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary" }}>
              Account &amp; Settings
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Manage your profile and security
            </Typography>
          </Box>
        </Stack>

        {/* PROFILE INFORMATION */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your verified identity and contact details
          </Typography>

          <Grid container spacing={3}>
            {/* Profile left */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
                <Avatar sx={{ width: 64, height: 64, fontSize: 22 }}>{initials}</Avatar>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {displayName}
                    </Typography>
                    {String(profile?.kycStatus || "").toLowerCase() === "verified" ? (
                      <Chip
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label="KYC verified"
                        sx={{
                          backgroundColor: (theme) => theme.palette.accent.success.bg,
                          color: (theme) => theme.palette.accent.success.text,
                          "& .MuiChip-icon": {
                            color: (theme) => theme.palette.accent.success.text,
                          },
                        }}
                      />
                    ) : profile?.kycStatus ? (
                      <Chip size="small" label={profile.kycStatus} color="warning" variant="outlined" />
                    ) : null}
                    {profile?.status ? (
                      <Chip size="small" label={profile.status} variant="outlined" sx={{ fontWeight: 600 }} />
                    ) : null}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {roleLabel ? `${roleLabel} account` : "Account"}
                    {profile?.investorType
                      ? ` · ${formatIdTypeLabel(profile.investorType)}`
                      : ""}
                  </Typography>
                  {profile?.nationality ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Nationality: {formatNationalityLabel(profile.nationality)}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              {/* Email / Phone */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Email Address
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">{mergedUser?.email || "—"}</Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Phone Number
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mt={0.5}
                  >
                    <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {profile?.phoneNumber != null && String(profile.phoneNumber).trim() !== ""
                        ? String(profile.phoneNumber).trim().startsWith("+")
                          ? String(profile.phoneNumber).trim()
                          : `+${String(profile.phoneNumber).trim()}`
                        : "—"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* Identity / Property cards */}
            <Grid size={{ xs: 12, md: 6 }}>
              {/* Emirates ID card */}
              {/* <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  backgroundColor: (theme) => theme.palette.accent.success.soft,
                  borderColor: (theme) => theme.palette.accent.success.border,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  ID type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatIdTypeLabel(profile?.idType)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile?.idNumber != null && String(profile.idNumber).trim() !== ""
                    ? `ID number: ${String(profile.idNumber).trim()}`
                    : "—"}
                </Typography>
              </Paper> */}

              <Grid container spacing={2}>
                {/* Identity Verification */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) => theme.palette.accent.success.softer,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      Identity Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nafath Authentication
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        profile?.verifiedByNafath
                          ? "Verified via Nafath"
                          : `Status: ${nafathStatusText}`
                      }
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          profile?.verifiedByNafath
                            ? theme.palette.accent.success.bg
                            : theme.palette.accent.warning.pendingBg,
                        color: (theme) =>
                          profile?.verifiedByNafath
                            ? theme.palette.accent.success.text
                            : theme.palette.accent.warning.pendingText,
                      }}
                    />
                    {profile?.nafathVerifiedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Verified on{" "}
                        {new Date(profile.nafathVerifiedAt).toLocaleDateString("en-GB")}
                      </Typography>
                    )}
                    {showNafathRejectionDetails && (
                      <Box sx={{ mt: 1.5 }}>
                        {nafathRejectionReason && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "error.main", mb: 0.75 }}
                          >
                            Rejection reason: {nafathRejectionReason}
                          </Typography>
                        )}
                        {nafathFilesNeedToBeUpdatedRaw.length > 0 && (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {nafathFilesNeedToBeUpdatedRaw.map((fileKey) => (
                              <Chip
                                key={`${fileKey}`}
                                size="small"
                                label={nafathFileLabel(fileKey)}
                                sx={{
                                  backgroundColor: (theme) =>
                                    theme.palette.accent.warning.pendingBg,
                                  color: (theme) => theme.palette.accent.warning.pendingText,
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                        {hasNafathDocUpdateRequest && (
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => setIsEditingNafathDocs((prev) => !prev)}
                            sx={{ textTransform: "none", mt: 0.5, px: 0 }}
                          >
                            {isEditingNafathDocs ? "Cancel edit" : "Edit rejected documents"}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Property Ownership */}
                <Grid size={{ xs: 12, md: 6 }}>
                  {/* <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      height: "100%",
                      backgroundColor: (theme) => theme.palette.accent.warning.cardBg,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      VARA compliance
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Virtual Assets Regulatory Authority
                    </Typography>
                    <Chip
                      size="small"
                      label={profile?.verifiedByVARA != null ? String(profile.verifiedByVARA) : "—"}
                      sx={{
                        mt: 1.5,
                        backgroundColor: (theme) =>
                          String(profile?.verifiedByVARA || "")
                            .toLowerCase()
                            .includes("approved") ||
                          String(profile?.verifiedByVARA || "")
                            .toLowerCase()
                            .includes("verified")
                            ? theme.palette.accent.success.bg
                            : theme.palette.chips.pending.bg,
                        color: (theme) =>
                          String(profile?.verifiedByVARA || "")
                            .toLowerCase()
                            .includes("approved") ||
                          String(profile?.verifiedByVARA || "")
                            .toLowerCase()
                            .includes("verified")
                            ? theme.palette.accent.success.text
                            : theme.palette.chips.pending.text,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {profile?.kycVerifiedAt
                        ? `KYC verified ${formatProfileDate(profile.kycVerifiedAt)}`
                        : " "}
                    </Typography>
                  </Paper> */}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {isEditingNafathDocs && hasNafathDocUpdateRequest && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                mt: 3,
                borderRadius: 2,
                p: 2,
                borderColor: (theme) => theme.palette.border.light,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Update Nafath Documents
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Upload only the documents requested by admin.
              </Typography>

              <Stack spacing={2}>
                {shouldUpdateIdProof && (
                  <Box>
                    <input
                      ref={idProofInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onNafathFilePicked(e, "idProofDocument")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => idProofInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                    >
                      Upload ID Proof
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {idProofFile ? idProofFile.name : "No file selected"}
                    </Typography>
                    {nafathDocErrors.idProofDocument && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {nafathDocErrors.idProofDocument}
                      </Typography>
                    )}
                  </Box>
                )}

                {shouldUpdateAddressProof && (
                  <Box>
                    <input
                      ref={addressProofInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      hidden
                      onChange={(e) => onNafathFilePicked(e, "addressProofDocument")}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => addressProofInputRef.current?.click()}
                      sx={{ textTransform: "none" }}
                    >
                      Upload Address Proof
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {addressProofFile ? addressProofFile.name : "No file selected"}
                    </Typography>
                    {nafathDocErrors.addressProofDocument && (
                      <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5 }}>
                        {nafathDocErrors.addressProofDocument}
                      </Typography>
                    )}
                  </Box>
                )}

                {nafathDocErrors.submit && (
                  <Typography variant="caption" color="error.main">
                    {nafathDocErrors.submit}
                  </Typography>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmitNafathDocuments}
                    disabled={isUploadingNafathDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    {isUploadingNafathDocuments ? "Uploading..." : "Submit Documents"}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setIsEditingNafathDocs(false)}
                    disabled={isUploadingNafathDocuments}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* KYC status banner */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: 2,
              p: 2,
              background:
                "linear-gradient(90deg, #e8f6ff 0%, #f0f9ff 50%, #e8fdfa 100%)",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              KYC status:
            </Typography>{" "}
            <Typography variant="caption" component="span">
              {profile?.kycStatus || "—"}
              {profile?.kycVerifiedAt
                ? ` · Verified on ${formatProfileDate(profile.kycVerifiedAt)}`
                : ""}
              {profile && !profile.kycVerifiedAt && profile.kycStatus
                ? ". Complete any pending checks in your verification cards above."
                : ""}
            </Typography>
          </Paper>
        </Paper>

        {/* MY WALLET + SECURITY */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, p: 3, mb: 3, backgroundColor: "background.paper" }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                My Wallet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your blockchain wallet and earnings overview
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AccountBalanceWalletOutlined />}
              onClick={() => setConnectOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: "#0d9488",
                "&:hover": { bgcolor: "#0f766e" },
              }}
            >
              {walletAddress ? "Change Wallet" : "Connect Wallet"}
            </Button>
          </Stack>

          {!walletAddress ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                borderColor: "#e5e7eb",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#e0f2fe",
                  display: "grid",
                  placeItems: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <AccountBalanceWalletOutlined sx={{ fontSize: 36, color: "#0284c7" }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                No Wallet Connected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: "auto" }}>
                Connect your crypto wallet to view your balance, earnings, and transaction history.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AccountBalanceWalletOutlined />}
                onClick={() => setConnectOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: "#0284c7",
                  "&:hover": { bgcolor: "#0369a1" },
                }}
              >
                Connect Wallet
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                Supported wallets: MetaMask, WalletConnect, and Coinbase Wallet
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper
                sx={{
                  borderRadius: 3,
                  p: 3,
                  background:
                    "linear-gradient(90deg,#06124c 0%,#071b5f 40%,#060e3d 100%)",
                  color: "#ffffff",
                  mb: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Wallet Address
                  </Typography>
                  <Stack direction="row" spacing={0.75}>
                    {apiWalletVerified && (
                      <Chip
                        label="Verified"
                        size="small"
                        sx={{ backgroundColor: "#22c55e", color: "#fff", fontWeight: 700 }}
                      />
                    )}
                    <Chip
                      label={walletProvider ? `${walletProvider} · Connected` : "Connected"}
                      size="small"
                      sx={{ backgroundColor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700 }}
                    />
                  </Stack>
                </Stack>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: 1, fontWeight: 600 }}
                >
                  {walletAddress}
                </Typography>
                <Typography variant="caption" sx={{ mt: 1.5, display: "block", opacity: 0.85 }}>
                  {networkLabel(WALLET_CHAIN_ID)} (Chain ID: {WALLET_CHAIN_ID})
                </Typography>
                {apiWalletVerifiedAt && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.7 }}>
                    Verified on {formatProfileDate(apiWalletVerifiedAt)}
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
                  <Button
                    component="a"
                    href={blockExplorerAddressUrl(walletAddress)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      color: "#ffffff",
                      borderColor: "rgba(255,255,255,0.45)",
                      textTransform: "none",
                      "&:hover": { borderColor: "#ffffff" },
                    }}
                  >
                    View on Explorer
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleDisconnectWallet}
                    sx={{
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      background: "#e91212",
                      "&:hover": { background: "#c0392b" },
                    }}
                  >
                    Disconnect
                  </Button>
                </Stack>
              </Paper>

              {/* <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: "Total Earnings", sub: "All-time earnings", value: "—", bg: "#ecfdf5", accent: "#047857" },
                  {
                    label: "Wallet Balance",
                    sub: "Current balance",
                    value: apiWalletBalance != null
                      ? `SAR ${new Intl.NumberFormat("en-SA", { maximumFractionDigits: 2 }).format(apiWalletBalance)}`
                      : "—",
                    bg: "#e0f2fe",
                    accent: "#0369a1",
                  },
                  { label: "This Month", sub: "Rental + dividends", value: "—", bg: "#f3e8ff", accent: "#7c3aed" },
                ].map((card) => (
                  <Grid size={{ xs: 12, md: 4 }} key={card.label} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: card.bg,
                        border: "1px solid rgba(0,0,0,0.04)",
                        flex: 1,
                        width: "100%",
                        minHeight: 120,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ color: card.accent }}>
                        {card.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ mt: 1, color: "#0f172a" }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
                        {card.sub}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid> */}
            </>
          )}

          <InvestorConnectWalletDialog
            open={connectOpen}
            onClose={() => setConnectOpen(false)}
            onLinked={handleWalletLinked}
          />

          {/* Security */}
          {/* <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Security
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage your account security settings
          </Typography> */}

          {false && (
            <Stack spacing={2}>
              {/* 2FA */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #edf0f7",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Two-Factor Authentication (2FA)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add an extra layer of security to your account
                  </Typography>
                </Box>
                <Switch
                  checked={twoFA}
                  onChange={(e) => setTwoFA(e.target.checked)}
                />
              </Box>

              {/* Biometric */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #edf0f7",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Biometric Authentication
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Use fingerprint or face recognition
                  </Typography>
                </Box>
                <Switch
                  checked={biometric}
                  onChange={(e) => setBiometric(e.target.checked)}
                />
              </Box>

              {/* Change password */}
              <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  sx={{ borderRadius: 999 }}
                >
                  Change Password
                </Button>
              </Box>
            </Stack>
          )}
        </Paper>

        {/* PREFERENCES + PRIVACY + INVESTMENT + NOTIFICATIONS */}
        {false && (
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, p: 3, mb: 3, backgroundColor: "background.paper" }}
          >
            {/* Preferences */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Customize your experience and regional settings
            </Typography>

            <Grid container spacing={3}>
              {/* Display & Regional */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  Display &amp; Regional
                </Typography>

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Language
                    </Typography>
                    <Typography variant="body2">English</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Display Currency
                    </Typography>
                    <Typography variant="body2">AED (د.إ)</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Time Zone
                    </Typography>
                    <Typography variant="body2">Dubai (UTC+4)</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date Format
                    </Typography>
                    <Typography variant="body2">DD/MM/YYYY</Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Appearance */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  Appearance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Theme
                </Typography>

                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {["Light", "Dark", "System"].map((label, idx) => (
                    <Grid size={{ xs: 12, md: 4 }} key={label}>
                      <Paper
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          px: 1,
                          textAlign: "center",
                          border:
                            idx === 0
                              ? "1.5px solid #00a3ff"
                              : "1px solid #e1e5f0",
                          backgroundColor: idx === 0 ? "#f2fbff" : "#ffffff",
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Privacy */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Privacy
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #edf0f7",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Portfolio Visibility
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Allow others to see your portfolio summary
                  </Typography>
                </Box>
                <Switch
                  checked={portfolioVisible}
                  onChange={(e) => setPortfolioVisible(e.target.checked)}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #edf0f7",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Activity Tracking
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Share anonymized usage data to improve platform
                  </Typography>
                </Box>
                <Switch
                  checked={activityTracking}
                  onChange={(e) => setActivityTracking(e.target.checked)}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #edf0f7",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Third-Party Data Sharing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Share data with VARA-approved partners
                  </Typography>
                </Box>
                <Switch
                  checked={thirdPartySharing}
                  onChange={(e) => setThirdPartySharing(e.target.checked)}
                />
              </Box>
            </Stack>

            {/* Investment preferences */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Investment Preferences
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Risk Profile
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Moderate
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #edf0f7",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Auto-Reinvest Dividends
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically reinvest rental income into new tokens
                    </Typography>
                  </Box>
                  <Switch
                    checked={autoReinvest}
                    onChange={(e) => setAutoReinvest(e.target.checked)}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #edf0f7",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      New Property Alerts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Notify when properties matching your preferences launch
                    </Typography>
                  </Box>
                  <Switch
                    checked={newPropertyAlerts}
                    onChange={(e) => setNewPropertyAlerts(e.target.checked)}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Minimum Investment Alert (AED)
              </Typography>
              <Box sx={{ maxWidth: 240, mt: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value="50000"
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Notifications */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose what updates you want to receive
            </Typography>

            {/* Channels */}
            <Typography variant="caption" color="text.secondary">
              Communication Channels
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    border: emailChannel
                      ? "1.5px solid #2952ff"
                      : "1px solid #e1e5f0",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">Email</Typography>
                    <Switch
                      size="small"
                      checked={emailChannel}
                      onChange={(e) => setEmailChannel(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    border: smsChannel
                      ? "1.5px solid #2952ff"
                      : "1px solid #e1e5f0",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">SMS</Typography>
                    <Switch
                      size="small"
                      checked={smsChannel}
                      onChange={(e) => setSmsChannel(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    border: pushChannel
                      ? "1.5px solid #2952ff"
                      : "1px solid #e1e5f0",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">Push</Typography>
                    <Switch
                      size="small"
                      checked={pushChannel}
                      onChange={(e) => setPushChannel(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Notification types */}
            <Typography variant="caption" color="text.secondary">
              Notification Types
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1, mb: 3 }}>
              {[
                {
                  label: "Payout Updates",
                  desc: "Get notified when rental income is distributed",
                  value: payoutUpdates,
                  set: setPayoutUpdates,
                },
                {
                  label: "Token Issuance",
                  desc: "Alerts when new tokens are minted to your wallet",
                  value: tokenIssuance,
                  set: setTokenIssuance,
                },
                {
                  label: "Compliance Alerts",
                  desc: "Important regulatory and compliance updates",
                  value: complianceAlerts,
                  set: setComplianceAlerts,
                },
                {
                  label: "Property Updates",
                  desc: "News about your invested properties",
                  value: propertyUpdates,
                  set: setPropertyUpdates,
                },
                {
                  label: "Marketing & Promotions",
                  desc: "New investment opportunities and offers",
                  value: marketingPromos,
                  set: setMarketingPromos,
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #edf0f7",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.desc}
                    </Typography>
                  </Box>
                  <Switch
                    checked={item.value}
                    onChange={(e) => item.set(e.target.checked)}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* SUPPORT + FAQ + REGULATORY + SAVE */}
        {false && (
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, p: 3, mb: 3, backgroundColor: "background.paper" }}
          >
            {/* Support */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Support
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Get help with your account and investments
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    border: "1px solid #e1e5f0",
                    textAlign: "left",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Email Support
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    support@mulk.ae
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Send Email
                  </Button>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  sx={{
                    borderRadius: 2,
                    p: 2.5,
                    border: "1px solid #e1e5f0",
                    textAlign: "left",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Help Center
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Browse FAQs and guides
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Visit Help Center
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            {/* FAQ */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Frequently Asked Questions
            </Typography>
            <Box>
              {[
                "How does VARA compliance protect my investment?",
                "What is the role of DLD in tokenization?",
                "How are rental payouts calculated?",
                "Can I sell my tokens on secondary markets?",
                "What happens with escrow funds at Zand Bank?",
              ].map((q) => (
                <Accordion
                  key={q}
                  elevation={0}
                  sx={{
                    borderRadius: 0,
                    borderBottom: "1px solid #edf0f7",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ px: 0 }}
                  >
                    <Typography variant="body2">{q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Placeholder answer for “{q}”. You can replace this with your
                      actual FAQ content.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Regulatory compliance banner */}
            <Paper
              sx={{
                mt: 4,
                borderRadius: 3,
                p: 3,
                background:
                  "linear-gradient(90deg,#071b5f 0%,#06124c 50%,#050c38 100%)",
                color: "#ffffff",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Regulatory Compliance
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5, mb: 2 }}>
                Your investments are protected under UAE regulatory framework
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label="VARA Compliant"
                  size="small"
                  sx={{ backgroundColor: "#00c26f", color: "#ffffff" }}
                />
                <Chip
                  label="Zand Bank Escrow"
                  size="small"
                  sx={{ backgroundColor: "#2f80ff", color: "#ffffff" }}
                />
              </Stack>
            </Paper>

            {/* Save changes banner */}
            <Paper
              sx={{
                mt: 3,
                borderRadius: 3,
                p: 2.5,
                background:
                  "linear-gradient(90deg,#e6fbff 0%,#f0fcff 50%,#e6fff9 100%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2">
                Save Your Changes
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Your settings will be applied immediately
                </Typography>
              </Typography>
              <Button
                variant="contained"
                sx={{ borderRadius: 999, px: 4, backgroundColor: "#2952ff" }}
              >
                Save Changes
              </Button>
            </Paper>
          </Paper>
          )}
      </Box>
    </Box>
  );
}

export default InvestorSettings;
